import type { HelpPair, HelpZettel, HelpZettelDetail } from '@/types/help'

const SCOPE_LABELS_DE: Record<string, string> = {
  'user.create': 'Benutzer anlegen',
  'user.read': 'Benutzer lesen',
  'user.update': 'Benutzer aktualisieren',
  'user.list': 'Benutzer listen',
  'role.view': 'Rollen ansehen',
  'school.read': 'Schule lesen',
  'school.update': 'Schule ändern',
  'school.settings.manage': 'Schuleinstellungen ändern',
  'teacher.create': 'Lehrpersonen anlegen',
  'teacher.read': 'Lehrpersonen lesen',
  'teacher.update': 'Lehrpersonen aktualisieren',
  'teacher.delete': 'Lehrpersonen löschen',
  'teacher.list': 'Lehrpersonen listen',
  'teacher.timetable.manage': 'Lehrer-Stundenplan verwalten',
  'teacher.qualification.manage': 'Qualifikationen verwalten',
  'timetable.create': 'Stundenplan anlegen',
  'timetable.read': 'Stundenplan lesen',
  'timetable.update': 'Stundenplan aktualisieren',
  'timetable.delete': 'Stundenplan löschen',
  'timetable.import': 'Stundenplan importieren',
  'timetable.parse': 'Stundenplan parsen',
  'substitution.read': 'Vertretungen lesen',
  'substitution.manage': 'Vertretungen verwalten',
  'substitution.book': 'Vertretungen buchen',
  'substitution.delete.booked': 'Gebuchte Vertretungen löschen',
  'substitution.request.on_behalf': 'Im Auftrag anfragen',
  'substitution.direct.assign': 'Admin-Stellvertretung',
  'substitution.request': 'Vertretung anfragen',
  'substitution.accept': 'Vertretung annehmen',
  'substitution.decline': 'Vertretung ablehnen',
  'substitution.cancel': 'Vertretung stornieren',
  'radar.view': 'Radar ansehen',
  'radar.view.all': 'Radar aller ansehen',
  'radar.interact': 'Radar bedienen',
  'holiday.create': 'Ferien anlegen',
  'holiday.read': 'Ferien lesen',
  'holiday.update': 'Ferien aktualisieren',
  'holiday.delete': 'Ferien löschen',
  'holiday.import': 'Ferien importieren',
  'parsing.configure': 'Parsing konfigurieren',
  'parsing.train': 'Parsing trainieren',
  'parsing.validate': 'Parsing validieren',
  'import.execute': 'Import ausführen',
  'notification.configure': 'Benachrichtigungen konfigurieren',
  'admin.dashboard.view': 'Admin-Dashboard ansehen',
  'superadmin.dashboard.view': 'Superadmin-Dashboard ansehen',
  'audit_log.view': 'Audit-Log ansehen',
  'hr.report.view': 'HR-Berichte ansehen',
  'hr.report.export': 'HR-Berichte exportieren',
  'hr.report.schedule': 'HR-Berichte planen',
  'hr.statistics.view': 'HR-Statistik ansehen',
  'hr.data.edit': 'HR-Daten bearbeiten',
  'director.decisions.view': 'Direktor-Entscheide ansehen',
  'director.decisions.create': 'Direktor-Entscheide treffen',
  'director.documents.request': 'Dokumente anfordern',
  'director.manual.assign': 'Manuell zuweisen',
  'director.case.adjust': 'Fall anpassen',
}

const SCOPE_LABELS_EN: Record<string, string> = {
  'user.create': 'Create users',
  'user.read': 'Read users',
  'user.update': 'Update users',
  'user.list': 'List users',
  'role.view': 'View roles',
  'school.read': 'Read school',
  'school.update': 'Update school',
  'school.settings.manage': 'Manage school settings',
  'teacher.create': 'Create teachers',
  'teacher.read': 'Read teachers',
  'teacher.update': 'Update teachers',
  'teacher.delete': 'Delete teachers',
  'teacher.list': 'List teachers',
  'teacher.timetable.manage': 'Manage teacher timetable',
  'teacher.qualification.manage': 'Manage qualifications',
  'timetable.create': 'Create timetable',
  'timetable.read': 'Read timetable',
  'timetable.update': 'Update timetable',
  'timetable.delete': 'Delete timetable',
  'timetable.import': 'Import timetable',
  'timetable.parse': 'Parse timetable',
  'substitution.read': 'Read substitutions',
  'substitution.manage': 'Manage substitutions',
  'substitution.book': 'Book substitutions',
  'substitution.delete.booked': 'Delete booked substitutions',
  'substitution.request.on_behalf': 'Request on behalf',
  'substitution.direct.assign': 'Admin substitution',
  'substitution.request': 'Request substitution',
  'substitution.accept': 'Accept substitution',
  'substitution.decline': 'Decline substitution',
  'substitution.cancel': 'Cancel substitution',
  'radar.view': 'View radar',
  'radar.view.all': 'View all radars',
  'radar.interact': 'Use radar',
  'holiday.create': 'Create holidays',
  'holiday.read': 'Read holidays',
  'holiday.update': 'Update holidays',
  'holiday.delete': 'Delete holidays',
  'holiday.import': 'Import holidays',
  'parsing.configure': 'Configure parsing',
  'parsing.train': 'Train parsing',
  'parsing.validate': 'Validate parsing',
  'import.execute': 'Run import',
  'notification.configure': 'Configure notifications',
  'admin.dashboard.view': 'View admin dashboard',
  'superadmin.dashboard.view': 'View superadmin dashboard',
  'audit_log.view': 'View audit log',
  'hr.report.view': 'View HR reports',
  'hr.report.export': 'Export HR reports',
  'hr.report.schedule': 'Schedule HR reports',
  'hr.statistics.view': 'View HR statistics',
  'hr.data.edit': 'Edit HR data',
  'director.decisions.view': 'View director decisions',
  'director.decisions.create': 'Make director decisions',
  'director.documents.request': 'Request documents',
  'director.manual.assign': 'Assign manually',
  'director.case.adjust': 'Adjust case',
}

export const TRANSCRIPT_PENDING_BODY = 'Transkript folgt'

export function pairFromZettel(z: HelpZettel): HelpPair {
  return {
    zettel_id: z.id,
    status: z.status,
    sender_role: z.sender_role,
    opener: {
      id: z.opener_id || z.id,
      kind: z.has_audio ? 'audio' : 'text',
      body: z.opener_preview || '',
      sender_id: '',
      sender_name: '',
      sender_role: z.sender_role,
      created_at: z.opener_created_at || z.last_activity_at || '',
      transcript_pending: z.transcript_pending,
      has_audio: z.has_audio,
      audio_duration_ms: z.audio_duration_ms,
      sender_scopes: [],
    },
    replies: [],
  }
}

export function seedZettelDetail(
  prev: HelpZettelDetail | undefined,
  zettel: HelpZettel,
  opener?: HelpPair['opener'],
): HelpZettelDetail {
  if (prev?.pairs?.length) {
    return { zettel: { ...prev.zettel, ...zettel }, pairs: prev.pairs }
  }
  const pair = pairFromZettel(zettel)
  if (opener) pair.opener = opener
  return { zettel, pairs: [pair] }
}

export function isTranscriptPending(
  pending?: boolean | null,
  body?: string | null,
  _hasAudio?: boolean | null,
): boolean {
  if (pending) return true
  return (body || '').trim() === TRANSCRIPT_PENDING_BODY
}

export function formatTalkTime(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

export function formatTalkDuration(ms?: number | null): string {
  if (ms == null || ms <= 0) return ''
  const s = Math.round(ms / 1000)
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${r.toString().padStart(2, '0')}`
}

export function formatScopeLabel(key: string, lang = 'de'): string {
  const map = lang.toLowerCase().startsWith('en') ? SCOPE_LABELS_EN : SCOPE_LABELS_DE
  return map[key] || key.replace(/_/g, ' ')
}
